import { config } from './config';
import type {
    CopyrightInput,
    CopyrightResult,
    CopyrightValidationError,
    CopyrightBuilder as CopyrightBuilderInterface,
    GeneratorOptions,
    SchemaGenerationResult,
    CompanySchemaValidationError,
} from './types/company';
import { Result, ok, fail, isOk, safeString, stripHtmlTags, isValidUrl, toAbsoluteUrl } from './utils';
import { logger } from './utils/logger';

const copyrightCache = new Map<string, CopyrightResult>();

function validateCopyrightInput(input: unknown): Result<CopyrightInput, CopyrightValidationError[]> {
    const errors: CopyrightValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'input', message: 'Copyright input must be an object' }]);
    }

    const copyright = input as Record<string, unknown>;

    if (!copyright.copyrightYear || typeof copyright.copyrightYear !== 'number') {
        errors.push({
            field: 'copyrightYear',
            message: 'Copyright year is required and must be a number',
            value: copyright.copyrightYear,
        });
    } else {
        const currentYear = new Date().getFullYear();
        const year = copyright.copyrightYear;
        if (year < 1900 || year > currentYear + 1) {
            errors.push({
                field: 'copyrightYear',
                message: `Copyright year must be between 1900 and ${currentYear + 1}`,
                value: year,
            });
        }
    }

    if (!copyright.copyrightHolder || typeof copyright.copyrightHolder !== 'string') {
        errors.push({
            field: 'copyrightHolder',
            message: 'Copyright holder is required and must be a string',
            value: copyright.copyrightHolder,
        });
    }

    if (copyright.licenseUrl && typeof copyright.licenseUrl === 'string') {
        const absoluteUrl = toAbsoluteUrl(copyright.licenseUrl);
        if (!isValidUrl(absoluteUrl)) {
            errors.push({
                field: 'licenseUrl',
                message: 'License URL must be a valid URL format',
                value: copyright.licenseUrl,
            });
        }
    }

    if (copyright.licenseName && !copyright.licenseUrl) {
        errors.push({
            field: 'licenseUrl',
            message: 'License URL is required when license name is provided',
            value: copyright.licenseUrl,
        });
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(copyright as unknown as CopyrightInput);
}

class CopyrightBuilder implements CopyrightBuilderInterface {
    private schema: Partial<CopyrightResult>;

    constructor() {
        this.schema = {
            '@type': 'CreativeWork',
        };
    }

    setCopyrightYear(year: number): CopyrightBuilder {
        if (year && year > 0) {
            this.schema.copyrightYear = year;
        }
        return this;
    }

    setCopyrightHolder(holder: string): CopyrightBuilder {
        if (holder) {
            if (holder === 'Storyblocks') {
                this.schema.copyrightHolder = config.getOrganizationSchema();
            } else {
                this.schema.copyrightHolder = stripHtmlTags(safeString(holder));
            }
        }
        return this;
    }

    setCopyrightNotice(notice: string): CopyrightBuilder {
        if (notice) {
            this.schema.copyrightNotice = stripHtmlTags(safeString(notice));
        }
        return this;
    }

    setLicense(name: string, url?: string): CopyrightBuilder {
        if (name) {
            if (url) {
                const absoluteUrl = toAbsoluteUrl(url);
                this.schema.license = {
                    '@type': 'CreativeWork',
                    name: stripHtmlTags(safeString(name)),
                    url: absoluteUrl,
                };
            } else {
                this.schema.license = stripHtmlTags(safeString(name));
            }
        }
        return this;
    }

    setUsageInfo(info: string): CopyrightBuilder {
        if (info) {
            this.schema.usageInfo = stripHtmlTags(safeString(info));
        }
        return this;
    }

    setAcquireLicensePage(url: string): CopyrightBuilder {
        if (url) {
            const absoluteUrl = toAbsoluteUrl(url);
            this.schema.acquireLicensePage = absoluteUrl;
        }
        return this;
    }

    build(): CopyrightResult {
        if (!this.schema.copyrightYear) {
            throw new Error('Copyright year is required');
        }

        if (!this.schema.copyrightHolder) {
            throw new Error('Copyright holder is required');
        }

        if (!this.schema.copyrightNotice) {
            const holderName =
                typeof this.schema.copyrightHolder === 'string'
                    ? this.schema.copyrightHolder
                    : (this.schema.copyrightHolder?.name as string | undefined) ?? 'Unknown';
            this.schema.copyrightNotice = `© ${this.schema.copyrightYear} ${holderName}. All rights reserved.`;
        }

        return this.schema as CopyrightResult;
    }
}

export function generateCopyrightSchema(
    input: CopyrightInput,
    options: GeneratorOptions = {},
): SchemaGenerationResult<CopyrightResult> {
    const cacheKey = `copyright-${JSON.stringify(input)}-${JSON.stringify(options)}`;

    if (!options.skipCache && copyrightCache.has(cacheKey)) {
        const cached = copyrightCache.get(cacheKey)!;
        logger.debug('Retrieved copyright schema from cache', { cacheKey });
        return {
            schema: cached,
            metadata: {
                generatedAt: new Date().toISOString(),
                cacheHit: true,
                validationPassed: true,
            },
        };
    }

    const validation = validateCopyrightInput(input);
    if (!isOk(validation)) {
        logger.warn('Invalid copyright input', { errors: validation.error });
        return {
            schema: {} as CopyrightResult,
            metadata: {
                generatedAt: new Date().toISOString(),
                cacheHit: false,
                validationPassed: false,
                errors: validation.error as CompanySchemaValidationError[],
            },
        };
    }

    const validInput = validation.data;

    const builder = new CopyrightBuilder();

    builder.setCopyrightYear(validInput.copyrightYear).setCopyrightHolder(validInput.copyrightHolder);

    if (validInput.copyrightNotice) {
        builder.setCopyrightNotice(validInput.copyrightNotice);
    }

    if (validInput.licenseName) {
        builder.setLicense(validInput.licenseName, validInput.licenseUrl);
    }

    if (validInput.usageInfo) {
        builder.setUsageInfo(validInput.usageInfo);
    }

    const schema = builder.build();

    if (!options.skipCache) {
        copyrightCache.set(cacheKey, schema);
    }

    logger.info('Generated copyright schema', {
        year: schema.copyrightYear,
        holder: typeof schema.copyrightHolder === 'string' ? schema.copyrightHolder : 'Organization',
        hasLicense: !!schema.license,
    });

    return {
        schema,
        metadata: {
            generatedAt: new Date().toISOString(),
            cacheHit: false,
            validationPassed: true,
        },
    };
}
